import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Collapse, 
  Typography, 
  Space, 
  Button, 
  Input, 
  Tag, 
  Divider,
  Alert,
  List,
  Tooltip,
  Tabs
} from 'antd';
import { 
  BookOutlined,
  SearchOutlined,
  VideoPlayOutlined,
  ToolOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  SafetyOutlined,
  TeamOutlined,
  FileTextOutlined,
  ApiOutlined,
  BugOutlined,
  ExperimentOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  MessageOutlined,
  SettingOutlined,
  CloudOutlined,
  MobileOutlined,
  DesktopOutlined,
  WarningOutlined
} from '@ant-design/icons';
import PageHeader from '../components/PageHeader';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Search } = Input;
const { TabPane } = Tabs;

export default function Help() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');

  // Comprehensive help content based on platform documentation
  const helpCategories = {
    'getting-started': {
      title: 'Getting Started',
      icon: <RocketOutlined />,
      color: '#52c41a',
      sections: [
        {
          title: 'Platform Overview',
          content: `Crisis Compass is a comprehensive emergency management platform providing real-time hazard monitoring across Australia through 15+ official government data sources. The system integrates crisis management, document collaboration, AI-powered insights, and automated workflows.`
        },
        {
          title: 'First Steps Checklist',
          type: 'list',
      items: [
            'Complete organization setup and user profile',
            'Upload your organization\'s sites and staff rosters',
            'Configure notification preferences and communication channels',
            'Explore the real-time hazard map with Australia-wide coverage',
            'Create your first incident and familiarize with the workflow',
            'Upload essential crisis management documents',
            'Set up SMS templates and communication protocols'
          ]
        },
        {
          title: 'User Roles & Permissions',
          content: `Crisis Compass uses role-based access control:
          
• **Staff**: Basic incident viewing and task management
• **Site Lead**: Site-specific management and coordination
• **Manager**: Organization-wide incident management and reporting
• **Executive**: Strategic oversight and executive summaries
• **Admin**: Full system administration and configuration`
        },
        {
          title: 'Navigation Basics',
          content: `The platform is organized into key sections:
          
• **Dashboard**: Overview of active incidents and metrics
• **Incidents**: Create and manage crisis incidents
• **Map**: Real-time hazard monitoring with 15+ data sources
• **Documents**: Collaborative document management with Office integration
• **AI Crisis Advisor**: AI-powered guidance and executive summaries`
        }
      ]
    },
    'incidents': {
      title: 'Incident Management',
      icon: <WarningOutlined />,
      color: '#ff4d4f',
      sections: [
        {
          title: 'Creating Incidents',
          content: `Create comprehensive incident records with:
          
• **Crisis Type Selection**: Choose from predefined types or create custom
• **Location Integration**: Set precise incident location with map integration
• **Timeline Management**: Track incident progression with milestones
• **Resource Assignment**: Assign teams, documents, and resources
• **Communication Setup**: Configure SMS and notification channels`
        },
        {
          title: 'Crisis Management Teams (CMT)',
          content: `Assign roles to crisis management team members:
          
• **CMT Leader**: Overall incident command and decision making
• **Deputy**: Support leader and backup command authority
• **Communications**: Media relations and public communications
• **Operations**: Field operations and tactical response
• **Planning**: Strategic planning and resource coordination
• **Logistics**: Supply chain and resource management`
        },
        {
          title: 'Task Management & Automation',
          content: `The platform provides automated task generation:
          
• **Crisis Task Engine**: Automatically generates relevant tasks based on incident type
• **YAML Templates**: Customizable task templates for different scenarios
• **Regulatory Compliance**: Automated regulatory deadline tracking
• **Smart Escalation**: AI-powered task prioritization and escalation
• **Dependency Mapping**: Visual task dependency relationships`
        },
        {
          title: 'Status Tracking',
          content: `Monitor incident progression through defined statuses:
          
• **Developing**: Initial incident assessment and setup
• **Active**: Full response mode with active management
• **Stabilising**: Incident under control, reducing response intensity
• **Resolved**: Incident concluded, post-incident activities
• **Exercise**: Training exercises and simulations`
        }
      ]
    },
    'hazard-monitoring': {
      title: 'Hazard Monitoring',
      icon: <GlobalOutlined />,
      color: '#1890ff',
      sections: [
        {
          title: 'Real-Time Data Sources (15 Active)',
          content: `Crisis Compass integrates with official Australian data sources:
          
**Weather & Environment**:
• OpenWeatherMap (8 cities, severe weather alerts)
• Bureau of Meteorology (weather warnings, floods, cyclones)
• BOM Marine Warnings (coastal hazards)
• EPA Air Quality (multi-state monitoring)
• ARPANSA UV Index (radiation monitoring)

**Fire & Emergency**:
• AFAC Emergency Services (bushfire incidents)
• Emergency Alert Australia (national broadcasts)
• ABC Emergency RSS (breaking news)
• State Fire Services (TAS, SA, QLD, NSW, VIC)

**Infrastructure & Natural Disasters**:
• Geoscience Australia (earthquakes)
• AEMO Power System (grid failures)
• Google Traffic (real-time incidents)`
        },
        {
          title: 'Map Features & Controls',
          content: `Interactive hazard monitoring capabilities:
          
• **Real-Time Updates**: 5-15 minute refresh intervals based on data priority
• **Severity Filtering**: Filter hazards by LOW/MEDIUM/HIGH/CRITICAL severity
• **Geographic Clustering**: Intelligent clustering of nearby hazards
• **Data Age Indicators**: "Last Verified" timestamps on all hazard popups
• **Proximity Alerts**: Monitor hazards within customizable distances from your sites
• **Layer Controls**: Toggle different hazard types on/off`
        },
        {
          title: 'Hazard Types & Classifications',
          content: `The system monitors multiple hazard categories:
          
• **WEATHER**: Severe weather warnings, temperature extremes
• **FIRE**: Bushfire incidents, fire danger ratings
• **EARTHQUAKE**: Seismic activity magnitude 2.5+
• **EMERGENCY**: Emergency service alerts and evacuations
• **MARINE**: Coastal hazards and marine weather
• **POWER**: Grid failures and infrastructure outages
• **TRAFFIC**: Real-time traffic incidents and road closures
• **AIR_QUALITY**: Pollution alerts and air hazards
• **UV**: UV radiation alerts`
        },
        {
          title: 'Performance & Reliability',
          content: `Optimized for crisis management needs:
          
• **Response Time**: 5-second map refresh (down from 30+ seconds)
• **API Efficiency**: 48 calls/day of 4000 limit (1.2% usage)
• **Error Handling**: Automatic recovery from provider failures
• **Caching System**: 4-hour cache to minimize API calls
• **Fallback Systems**: Multiple endpoint strategies for reliability`
        }
      ]
    },
    'ai-features': {
      title: 'AI-Powered Features',
      icon: <ExperimentOutlined />,
      color: '#722ed1',
      sections: [
        {
          title: 'AI Crisis Advisor',
          content: `GPT-4-turbo powered executive summaries and guidance:
          
• **Executive Summaries**: Natural language analysis covering WHAT, WHERE, WHEN, CAUSE, IMPACT, and RESPONSE
• **Contextual Intelligence**: Australian crisis management context
• **Real-time Analysis**: Integration with risk scoring and metrics
• **Cost Optimization**: 5-minute caching, estimated $10-50/month
• **Fallback System**: Automatic fallback to rule-based analysis if AI fails`
        },
        {
          title: 'Smart Automation Features',
          content: `AI-enhanced automation throughout the platform:
          
• **Smart Task Generation**: AI-powered task creation based on incident type
• **Intelligent Escalation**: Automated priority assignment and escalation
• **Predictive Analytics**: Early warning systems for incident development
• **Communication Intelligence**: Smart SMS template suggestions
• **Resource Optimization**: AI-driven resource allocation recommendations`
        },
        {
          title: 'AI Security & Privacy',
          content: `Enterprise-grade AI security measures:
          
• **Server-Side Processing**: API keys stored server-side only
• **No Client Exposure**: Secure server-to-OpenAI communication
• **Admin Notifications**: Automatic alerts for AI system failures
• **Data Privacy**: Only incident metadata sent to AI, no sensitive personal data
• **Audit Trail**: Complete logging of AI interactions and decisions`
        }
      ]
    },
    'communications': {
      title: 'Communications & SMS',
      icon: <MessageOutlined />,
      color: '#fa8c16',
      sections: [
        {
          title: 'SMS Communication System',
          content: `Comprehensive SMS capabilities powered by Twilio:
          
• **Bulk SMS by Location**: Send messages filtered by Site, City/State, Country, Region, and Role
• **Template Management**: Create and manage organization-specific SMS templates
• **Delivery Tracking**: Monitor message delivery status and responses
• **Staff Filtering**: Target specific groups based on location and role criteria
• **Emergency Broadcasts**: Rapid communication during crisis situations`
        },
        {
          title: 'Bulk SMS Features (Phase 1)',
          content: `Advanced staff communication filtering:
          
• **Geographic Filtering**: Filter staff by Country, State, Site, and Region
• **Role-Based Targeting**: Send messages to specific roles (Staff, Site Lead, Manager, Executive)
• **Preview Mode**: See exactly who will receive messages before sending
• **Real-Time Filtering**: Dynamic filtering with live staff counts
• **Audit Logging**: Complete record of all bulk communications`
        },
        {
          title: 'Message Templates',
          content: `Professional SMS template system:
          
• **Pre-built Templates**: Crisis-specific message templates
• **Custom Templates**: Create organization-specific messaging
• **Variable Insertion**: Dynamic content like names, locations, times
• **Character Counting**: Smart SMS segment calculation
• **Template Categories**: Organize by incident type and urgency level`
        },
        {
          title: 'Integration & Setup',
          content: `Communication system configuration:
          
• **Twilio Integration**: Enterprise-grade SMS delivery platform
• **Phone Number Setup**: Configure sender phone numbers
• **Two-Way SMS**: Receive and manage SMS replies
• **Delivery Reports**: Track message delivery and response rates
• **Compliance**: Message archiving and regulatory compliance features`
        }
      ]
    },
    'documents': {
          title: 'Document Management',
      icon: <FileTextOutlined />,
      color: '#13c2c2',
      sections: [
        {
          title: 'Collaborative Document Editing',
          content: `Professional document workflow without expensive licensing:
          
• **Edit in Office**: One-click download and edit in desktop Office applications
• **Session Tracking**: Monitor who's editing documents in real-time
• **Version Control**: Automatic version history and change tracking
• **Collaboration Status**: Live indicators showing document editing status
• **Smart Upload**: Guided workflow for uploading changes back to the system`
        },
        {
          title: 'Document Organization',
          content: `Comprehensive document management:
          
• **Organization Documents**: System-wide documents available to all users
• **Incident-Linked Documents**: Documents specific to individual incidents
• **Categories & Tags**: Organize documents by type and purpose
• **Search & Filtering**: Powerful search across document content and metadata
• **Access Control**: Role-based permissions for document access and editing`
        },
        {
          title: 'Office Integration',
          content: `Seamless integration with Microsoft Office:
          
• **Supported Formats**: Word (.docx), Excel (.xlsx), PowerPoint (.pptx)
• **Office Online Viewer**: In-browser document preview
• **Desktop Editing**: Download, edit in desktop apps, upload changes
• **Version Tracking**: Automatic version control with change summaries
• **Audit Trail**: Complete record of document access and modifications`
        },
        {
          title: 'File Management Features',
          content: `Advanced file handling capabilities:
          
• **Secure Storage**: Firebase Cloud Storage with enterprise security
• **Large File Support**: Support for documents up to 10MB
• **Metadata Tracking**: Author, creation date, modification history
• **Download Analytics**: Track document access and usage patterns
• **Backup & Recovery**: Automatic backup of all document versions`
        }
      ]
    },
    'user-management': {
      title: 'User & Organization Management',
      icon: <TeamOutlined />,
      color: '#eb2f96',
      sections: [
        {
          title: 'Staff Management',
          content: `Comprehensive staff roster management:
          
• **Bulk Staff Upload**: CSV import with validation and error handling
• **Staff Roles**: Staff, Site Lead, Manager, Executive role assignments
• **Location Data**: Staff assignment to sites with geographic metadata
• **Contact Information**: Phone, email, emergency contacts, next of kin
• **Employee Data**: Employee IDs, start dates, department assignments, manager relationships`
        },
        {
          title: 'Site Management',
          content: `Geographic site organization:
          
• **Site Registration**: Detailed site information with addresses and coordinates
• **Geographic Hierarchy**: Country > State > Region > Site organization
• **Site Types**: Clinic, warehouse, office, facility type classification
• **Manager Assignment**: Site leads and contact information
• **Bulk Upload**: CSV import for multiple sites with validation`
        },
        {
          title: 'Organization Setup',
          content: `Complete organization configuration:
          
• **Organization Profile**: Company details, branding, contact information
• **Subscription Management**: Feature access and billing management
• **Digital Products**: Crisis management templates and resources
• **Communication Setup**: SMS configuration and template management
• **Compliance Settings**: Regulatory requirements and deadline tracking`
        },
        {
          title: 'Access Control & Security',
          content: `Enterprise security features:
          
• **Role-Based Access**: Granular permissions by user role
• **Multi-Factor Authentication**: Enhanced security for admin accounts
• **Audit Logging**: Complete activity tracking and compliance reporting
• **Data Encryption**: End-to-end encryption for sensitive information
• **Session Management**: Secure login and session timeout controls`
        }
      ]
    },
    'automation': {
      title: 'Automation & Workflows',
      icon: <ThunderboltOutlined />,
      color: '#f5222d',
      sections: [
        {
          title: 'Crisis Task Automation',
          content: `Intelligent task generation and management:
          
• **Crisis Task Engine**: YAML-based template system for automatic task creation
• **Task Dependencies**: Visual mapping of task relationships and prerequisites
• **Smart Escalation**: AI-powered priority assignment and escalation rules
• **Regulatory Integration**: Automatic regulatory deadline tracking and compliance tasks
• **CMT Assignment**: Automated assignment to Crisis Management Team roles`
        },
        {
          title: 'Regulatory Compliance Automation',
          content: `Automated regulatory deadline management:
          
• **Deadline Tracking**: Automatic monitoring of regulatory requirements
• **Map Visualization**: Geographic display of deadlines relative to sites
• **Urgency Alerts**: Critical (≤7 days), High (≤30 days), Medium (≤90 days), Low (≤365 days)
• **Compliance Reporting**: Automated compliance status reporting
• **Integration**: Seamless integration with incident management workflows`
        },
        {
          title: 'Smart Notifications',
          content: `Intelligent notification system:
          
• **Automated Alerts**: Smart alerts based on incident development
• **Escalation Rules**: Automatic escalation based on response times
• **Multi-Channel**: SMS, email, and in-app notifications
• **Customizable Rules**: Organization-specific automation rules
• **Machine Learning**: Adaptive algorithms that learn from user behavior`
        },
        {
          title: 'Workflow Optimization',
          content: `Performance and efficiency features:
          
• **Error Handling**: Graceful degradation when automation services fail
• **Permission Management**: Safe operations with automatic permission checking
• **Performance Monitoring**: Real-time monitoring of automation system health
• **Fallback Systems**: Manual override and fallback options for all automated processes`
        }
      ]
    },
    'troubleshooting': {
      title: 'Troubleshooting',
      icon: <BugOutlined />,
      color: '#faad14',
      sections: [
        {
          title: 'Common Issues & Solutions',
          type: 'qa',
      items: [
            {
              q: 'Hazard map not loading or showing outdated data',
              a: 'Check your internet connection and refresh the page. The system uses multiple data sources with automatic failover. If issues persist, some government APIs may be temporarily unavailable.'
            },
            {
              q: 'SMS messages not sending',
              a: 'Verify your organization has SMS credits and Twilio is configured. Check that recipient phone numbers include country codes (+61 for Australia). Contact support if delivery issues persist.'
            },
            {
              q: 'Document editing not working',
              a: 'Ensure you have Microsoft Office installed on your computer. The "Edit in Office" feature requires desktop Office applications. Browser-based editing is not supported due to licensing costs.'
            },
            {
              q: 'AI summaries showing "Rule-Based" instead of "AI-Powered"',
              a: 'This indicates the OpenAI integration is temporarily unavailable. The system automatically falls back to rule-based analysis. AI functionality will resume automatically when service is restored.'
            },
            {
              q: 'Permission errors when creating incidents or tasks',
              a: 'Contact your organization administrator to verify your user role permissions. Some features require CMT (Crisis Management Team) role access or higher.'
            },
            {
              q: 'Location autocomplete not working',
              a: 'The location input uses Google Places API. Check your internet connection and try typing more characters. If using mobile, ensure location services are enabled for your browser.'
            }
          ]
        },
        {
          title: 'Performance Issues',
          content: `If experiencing slow performance:
          
• **Clear Browser Cache**: Hard refresh with Ctrl+Shift+R (or Cmd+Shift+R on Mac)
• **Check Internet Connection**: Ensure stable connection for real-time features
• **Browser Compatibility**: Use Chrome, Firefox, Safari, or Edge (latest versions)
• **Mobile Performance**: Some features work best on desktop/tablet screens
• **Data Loading**: Large datasets may take time to load initially`
        },
        {
          title: 'Data Sync Issues',
          content: `For real-time data synchronization problems:
          
• **Refresh Page**: Force reload to reconnect to real-time data streams
• **Check Permissions**: Ensure your user role has access to the data you're trying to view
• **Network Issues**: Corporate firewalls may block WebSocket connections
• **Multiple Tabs**: Avoid having multiple Crisis Compass tabs open simultaneously
• **Browser Storage**: Clear browser storage if experiencing persistent sync issues`
        }
      ]
    },
    'api-integration': {
      title: 'API & Technical Integration',
      icon: <ApiOutlined />,
      color: '#2f54eb',
      sections: [
        {
          title: 'Data Source Architecture',
          content: `Crisis Compass uses a sophisticated multi-source architecture:
          
• **15 Active Data Sources**: Government and commercial APIs
• **Cloud Function Proxies**: Firebase Cloud Functions for API aggregation
• **Caching System**: 4-hour cache for government APIs, 5-minute for real-time data
• **Error Recovery**: Automatic failover and retry mechanisms
• **Rate Limiting**: Intelligent rate limiting to prevent API quota exhaustion`
        },
        {
          title: 'Firestore Database Structure',
          content: `Key collections and data organization:
          
• **c3_incidents**: Incident records and management data
• **c3_users**: User profiles and role assignments
• **c3_sites**: Geographic site information
• **c3_tasks**: Task management and automation
• **c3_documents**: Document metadata and version control
• **c3_hazards**: Real-time hazard data cache
• **c3_sms_messages**: Communication logs and delivery tracking`
        },
        {
          title: 'Security & Authentication',
          content: `Enterprise security implementation:
          
• **Firebase Authentication**: Secure user authentication and session management
• **Firestore Security Rules**: Granular access control at database level
• **API Key Management**: Server-side API key storage and rotation
• **CORS Configuration**: Secure cross-origin resource sharing
• **Data Encryption**: Encryption at rest and in transit for all sensitive data`
        },
        {
          title: 'Deployment & Infrastructure',
          content: `Cloud infrastructure and deployment:
          
• **Firebase Hosting**: Global CDN for fast content delivery
• **Cloud Functions**: Serverless backend processing
• **Cloud Storage**: Secure file storage and document management
• **Real-time Database**: WebSocket connections for live updates
• **Monitoring**: Application performance monitoring and error tracking`
        }
      ]
    }
  };

  const quickActions = [
    {
      title: 'AI Crisis Advisor',
      description: 'Get instant AI-powered guidance for crisis management',
      icon: <ExperimentOutlined />,
      color: '#722ed1',
      action: () => navigate('/ai-crisis-adviser')
    },
    {
      title: 'Create Incident',
      description: 'Start managing a new crisis incident',
      icon: <WarningOutlined />,
      color: '#ff4d4f',
      action: () => navigate('/incidents/new')
    },
    {
      title: 'View Hazard Map',
      description: 'Monitor real-time hazards across Australia',
      icon: <GlobalOutlined />,
      color: '#1890ff',
      action: () => navigate('/map')
    },
    {
      title: 'System Status',
      description: 'Check platform health and data source status',
      icon: <CloudOutlined />,
      color: '#52c41a',
      action: () => window.open('https://status.crisiscompass.com.au', '_blank')
    }
  ];

  const searchableContent = Object.entries(helpCategories).flatMap(([key, category]) =>
    category.sections.flatMap(section => ({
      category: key,
      categoryTitle: category.title,
      title: section.title,
      content: section.content || '',
      items: section.items || [],
      type: section.type
    }))
  );

  const filteredContent = searchTerm
    ? searchableContent.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryTitle.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const renderContent = (section) => {
    switch (section.type) {
      case 'list':
        return (
          <List
            size="small"
            dataSource={section.items}
            renderItem={item => (
              <List.Item>
                <Text>• {item}</Text>
              </List.Item>
            )}
          />
        );
      case 'qa':
        return (
          <Collapse size="small" ghost>
            {section.items.map((item, index) => (
              <Panel header={<Text strong>{item.q}</Text>} key={index}>
                <Paragraph style={{ margin: 0 }}>{item.a}</Paragraph>
              </Panel>
            ))}
          </Collapse>
        );
      default:
        return (
          <Paragraph style={{ whiteSpace: 'pre-line' }}>
            {section.content}
          </Paragraph>
        );
    }
  };

  return (
    <div className="p-4">
      <PageHeader title="Help & Support" />

      {/* Search */}
      <Card style={{ marginBottom: '24px' }}>
        <Search
          placeholder="Search help topics, features, or troubleshooting..."
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: '16px' }}
        />
        
        {searchTerm && (
          <div>
            <Text type="secondary">
              Found {filteredContent.length} results for "{searchTerm}"
            </Text>
            {filteredContent.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                {filteredContent.map((item, index) => (
                  <Card 
                    key={index} 
                    size="small" 
                    style={{ marginBottom: '8px' }}
                    title={
                      <Space>
                        <Text strong>{item.title}</Text>
                        <Tag color={helpCategories[item.category].color}>
                          {item.categoryTitle}
                        </Tag>
                      </Space>
                    }
                  >
                    <Text>{item.content.substring(0, 200)}...</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setActiveCategory(item.category);
                        }}
                      >
                        View Full Section
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {!searchTerm && (
        <>
          {/* Quick Actions */}
          <Card title="Quick Actions" style={{ marginBottom: '24px' }}>
            <div className="row g-3">
              {quickActions.map((action, index) => (
                <div key={index} className="col-md-3">
                  <Card
                    hoverable
                    onClick={action.action}
                    style={{ textAlign: 'center', height: '100%' }}
                  >
                    <div style={{ fontSize: '32px', color: action.color, marginBottom: '12px' }}>
                      {action.icon}
                      </div>
                    <Title level={5} style={{ marginBottom: '8px' }}>
                      {action.title}
                    </Title>
                    <Text type="secondary">{action.description}</Text>
                  </Card>
                </div>
              ))}
            </div>
          </Card>

                    {/* Main Help Content */}
          <div className="help-support-tabs">
            <style>{`
              .help-support-tabs .ant-tabs-tab {
                border: 2px solid #39325b !important;
                margin: 0 2px !important;
                border-radius: 6px !important;
                background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
                box-shadow: 0 2px 4px rgba(57, 50, 91, 0.1) !important;
                transition: all 0.3s ease !important;
                min-width: 120px !important;
                text-align: center !important;
                padding: 5px 11px !important;
                height: auto !important;
                line-height: 1.15 !important;
              }
              
              .help-support-tabs .ant-tabs-tab:hover {
                border-color: #39325b !important;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
                box-shadow: 0 4px 8px rgba(57, 50, 91, 0.2) !important;
                transform: translateY(-1px) !important;
              }
              
              .help-support-tabs .ant-tabs-tab-active {
                border: 3px solid #39325b !important;
                background: linear-gradient(135deg, #39325b 0%, #4a3f6b 100%) !important;
                color: white !important;
                font-weight: 700 !important;
                box-shadow: 0 6px 12px rgba(57, 50, 91, 0.3) !important;
              }
              
              .help-support-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                color: white !important;
              }
              
              .help-support-tabs .ant-tabs-tab-active .ant-tabs-tab-btn span {
                color: white !important;
              }
              
              .help-support-tabs .ant-tabs-content-holder {
                border: 2px solid #d9d9d9;
                border-radius: 6px;
                background: white;
                padding: 13px;
                margin-top: 5px;
              }
              
              .help-support-tabs .ant-tabs-nav {
                margin-bottom: 0 !important;
              }
              
              .help-support-tabs .ant-tabs-nav-wrap {
                padding: 5px 4px !important;
              }
              
              .help-support-tabs .ant-tabs-tab-btn {
                font-size: 12px !important;
                font-weight: 600 !important;
                padding: 2px 4px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                width: 100% !important;
              }
              
              /* Responsive adjustments for many tabs */
              @media (max-width: 1200px) {
                .help-support-tabs .ant-tabs-tab {
                  min-width: 100px !important;
                  margin: 0 1px !important;
                  padding: 4px 9px !important;
                }
              }
              
              @media (max-width: 768px) {
                .help-support-tabs .ant-tabs-tab {
                  min-width: 80px !important;
                  margin: 0 1px !important;
                  padding: 3px 7px !important;
                }
                .help-support-tabs .ant-tabs-tab-btn {
                  font-size: 11px !important;
                }
              }
            `}</style>
            
            <Card 
              style={{ 
                marginBottom: 0,
                paddingBottom: 0,
                border: '2px solid #d9d9d9',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                backgroundColor: '#f5f5f5'
              }}
            >
              <div style={{ paddingBottom: 0, padding: '5px' }}>
                <Tabs 
                  activeKey={activeCategory} 
                  onChange={setActiveCategory}
                  className="help-support-tabs"
                  type="card"
                  size="large"
                  style={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                  }}
                  tabBarStyle={{
                    marginBottom: '5px',
                    border: 'none'
                  }}
                  items={Object.entries(helpCategories).map(([key, category]) => ({
                    key: key,
                    label: (
                      <Tooltip title={`Learn about ${category.title.toLowerCase()} features and capabilities`}>
                        <Space>
                          <span style={{ color: activeCategory === key ? 'white' : category.color }}>
                            {category.icon}
                          </span>
                          {category.title}
                        </Space>
                      </Tooltip>
                    ),
                    children: (
                      <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '16px' }}>
                        {category.sections.map((section, index) => (
                          <div key={index} style={{ marginBottom: '32px' }}>
                            <Title level={4} style={{ color: category.color }}>
                              {section.title}
                            </Title>
                            {renderContent(section)}
                            {index < category.sections.length - 1 && <Divider />}
          </div>
        ))}
      </div>
                    )
                  }))}
                />
              </div>
            </Card>
          </div>

          {/* Contact Support */}
          <Card 
            title="Need Additional Help?" 
            style={{ marginTop: '24px' }}
          >
            <Alert
              message="24/7 Emergency Support Available"
              description="For critical incidents requiring immediate assistance, our emergency support team is available around the clock."
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <div className="row g-3">
              <div className="col-md-6">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ExperimentOutlined />}
                      onClick={() => navigate('/ai-crisis-adviser')}
                    style={{ 
                      width: '100%', 
                      backgroundColor: '#39325b', 
                      borderColor: '#39325b' 
                    }}
                  >
                    Ask AI Crisis Advisor
                  </Button>
                  <Text type="secondary">
                    Get instant AI-powered guidance for your specific crisis management questions
                  </Text>
                </Space>
              </div>
              <div className="col-md-6">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="default"
                    size="large"
                    icon={<MessageOutlined />}
                    style={{ width: '100%' }}
                    onClick={() => window.open('mailto:support@crisiscompass.com.au?subject=Crisis Compass Support Request', '_blank')}
                  >
                    Contact Support Team
                  </Button>
                  <Text type="secondary">
                    Email our expert support team for personalized assistance and technical help
                  </Text>
                </Space>
              </div>
            </div>

            <Divider />

            <div className="row g-3">
              <div className="col-md-4">
                <Card size="small">
                  <Paragraph>
                    <Text strong>Emergency Response:</Text><br />
                    <Text>Within 15 minutes</Text>
                  </Paragraph>
                </Card>
              </div>
              <div className="col-md-4">
                <Card size="small">
                  <Paragraph>
                    <Text strong>Standard Support:</Text><br />
                    <Text>Within 4 hours</Text>
                  </Paragraph>
                </Card>
          </div>
              <div className="col-md-4">
                <Card size="small">
                  <Paragraph>
                    <Text strong>Training & Setup:</Text><br />
                    <Text>Scheduled sessions available</Text>
                  </Paragraph>
                </Card>
        </div>
      </div>
          </Card>
        </>
      )}
    </div>
  );
} 